/* Virtual try-on. Opens a full-screen camera view, tracks the body with MediaPipe (runs in the
   browser, nothing is sent or recorded) and draws a cutout of the piece where it is worn:
   ring on a finger, bracelet on the wrist (hand tracking), chain or pendant on the neck (pose
   tracking), earrings on both lobes (face tracking). Triggered by [data-ice-tryon]. */
(() => {
  const VISION = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14';
  const MODELS = 'https://storage.googleapis.com/mediapipe-models/';
  // which tracker each kind of piece needs
  const TASKS = {
    hand: { cls: 'HandLandmarker', model: 'hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task', opts: { numHands: 1 } },
    pose: { cls: 'PoseLandmarker', model: 'pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task', opts: { numPoses: 1 } },
    face: { cls: 'FaceLandmarker', model: 'face_landmarker/face_landmarker/float16/1/face_landmarker.task', opts: { numFaces: 1 } },
  };
  const KIND_TASK = { ring: 'hand', wrist: 'hand', neck: 'pose', ear: 'face' };
  // finger -> [base knuckle, middle joint, neighbouring knuckle used to estimate finger width]
  const FINGERS = { index: [5, 6, 9], middle: [9, 10, 5], ring: [13, 14, 9], pinky: [17, 18, 13] };

  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);
  const trackers = {};
  const loadTracker = async (name) => {
    if (trackers[name]) return trackers[name];
    const task = TASKS[name];
    const vision = await withTimeout(import(VISION + '/vision_bundle.mjs'), 20000);
    const files = await withTimeout(vision.FilesetResolver.forVisionTasks(VISION + '/wasm'), 30000);
    const make = (delegate) =>
      withTimeout(
        vision[task.cls].createFromOptions(files, {
          baseOptions: { modelAssetPath: MODELS + task.model, delegate },
          runningMode: 'VIDEO',
          ...task.opts,
        }),
        45000
      );
    // iPhone / iPad Safari: the GPU delegate can hang instead of failing, so go straight to CPU
    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (ios) {
      trackers[name] = await make('CPU');
    } else {
      try {
        trackers[name] = await make('GPU');
      } catch (e) {
        trackers[name] = await make('CPU');
      }
    }
    return trackers[name];
  };
  // landmarks of the first hand / body / face in a detection result
  const firstLandmarks = (res) => res && ((res.landmarks && res.landmarks[0]) || (res.faceLandmarks && res.faceLandmarks[0]));

  const lerp = (a, b, k) => a + (b - a) * k;
  const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
  // angle that turns the cutout so its "down" points along (dx, dy)
  const downAngle = (dx, dy) => Math.atan2(dy, dx) - Math.PI / 2;

  /* Where to draw the piece, in canvas pixels. Returns a list of placements
     { x, y, ang, w, top, flip }: (x, y) is the centre of the cutout, or its top edge when `top`
     is set (pieces that hang); w is the drawn width before the product's own scale. */
  const place = (kind, lm, P, finger) => {
    if (kind === 'ring') {
      const [a, b, n] = FINGERS[finger];
      const base = P(a), mid = P(b), nb = P(n);
      return [{
        x: lerp(base.x, mid.x, 0.42),
        y: lerp(base.y, mid.y, 0.42),
        ang: Math.atan2(mid.y - base.y, mid.x - base.x) + Math.PI / 2,
        w: dist(nb, base),
      }];
    }
    if (kind === 'wrist') {
      // just below the wrist joint, across the forearm, as wide as the palm
      const W = P(0), M = P(9);
      const len = dist(W, M) || 1;
      const dx = (M.x - W.x) / len, dy = (M.y - W.y) / len;
      return [{
        x: W.x - dx * len * 0.1,
        y: W.y - dy * len * 0.1,
        ang: Math.atan2(dy, dx) + Math.PI / 2,
        w: dist(P(5), P(17)) * 1.05,
      }];
    }
    if (kind === 'neck') {
      // pose: 9/10 mouth corners, 11/12 shoulders; the chain sits around the base of the neck
      const L = P(11), R = P(12);
      if ((lm[11].visibility ?? 1) < 0.5 || (lm[12].visibility ?? 1) < 0.5) return [];
      const sm = { x: (L.x + R.x) / 2, y: (L.y + R.y) / 2 };
      const mo = { x: (P(9).x + P(10).x) / 2, y: (P(9).y + P(10).y) / 2 };
      const shoulders = dist(L, R);
      let sx = R.x - L.x, sy = R.y - L.y;
      if (sx < 0) { sx = -sx; sy = -sy; }
      return [{
        x: lerp(sm.x, mo.x, 0.55),
        y: lerp(sm.y, mo.y, 0.55),
        ang: Math.atan2(sy, sx),
        w: shoulders * 0.42,
        top: true,
      }];
    }
    if (kind === 'ear') {
      // face mesh: 234 / 454 face edges at ear level, 132 / 361 jaw below them, 10 forehead, 152 chin, 1 nose tip
      const e1 = P(234), e2 = P(454);
      const fw = dist(e1, e2) || 1;
      const top = P(10), chin = P(152), nose = P(1);
      const ang = downAngle(chin.x - top.x, chin.y - top.y);
      // how far the head is turned: the ear on the far side is hidden behind the face
      const turn = dist(nose, e1) / (dist(nose, e1) + dist(nose, e2));
      const out = { x: (e1.x - e2.x) / fw, y: (e1.y - e2.y) / fw };
      const lobe = (edge, jaw, sign) => {
        const p = { x: lerp(edge.x, jaw.x, 0.55), y: lerp(edge.y, jaw.y, 0.55) };
        return { x: p.x + sign * out.x * fw * 0.03, y: p.y + sign * out.y * fw * 0.03 };
      };
      const res = [];
      const l1 = lobe(e1, P(132), 1), l2 = lobe(e2, P(361), -1);
      if (turn > 0.22) res.push({ x: l1.x, y: l1.y, ang, w: fw * 0.1, top: true, flip: e1.x > e2.x });
      if (turn < 0.78) res.push({ x: l2.x, y: l2.y, ang, w: fw * 0.1, top: true, flip: e2.x > e1.x });
      return res;
    }
    return [];
  };

  // exposed for testing on a still photo
  window.__iceTryonPlace = { place, loadTracker, firstLandmarks };

  // ---------- 3D mode (Three.js): a GLB ring placed on the finger in 3D ----------
  const THREE_URL = 'https://esm.sh/three@0.160.0';
  const make3D = async (canvas, modelUrl, opts) => {
    const THREE = await import(THREE_URL);
    const { GLTFLoader } = await import(THREE_URL + '/examples/jsm/loaders/GLTFLoader.js');
    const { RoomEnvironment } = await import(THREE_URL + '/examples/jsm/environments/RoomEnvironment.js');
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, premultipliedAlpha: false });
    renderer.setPixelRatio(1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.35;
    const scene = new THREE.Scene();
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    const key = new THREE.DirectionalLight(0xffffff, 1.6);
    key.position.set(0.3, 1, 1);
    scene.add(key);
    const camera = new THREE.OrthographicCamera(0, 1, 0, -1, -10000, 10000);
    camera.position.z = 5000;

    const gold = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(opts.color || '#e9b949'),
      metalness: 1,
      roughness: 0.16,
      clearcoat: 0.4,
      clearcoatRoughness: 0.1,
    });
    const gltf = await new GLTFLoader().loadAsync(modelUrl);
    const model = gltf.scene;
    model.traverse((o) => {
      if (o.isMesh) {
        o.material = gold;
        o.geometry.computeVertexNormals();
      }
    });
    // normalise: centre the ring, then turn it so its hole runs along Y and its face looks at +Z
    const box = new THREE.Box3().setFromObject(model);
    const c = box.getCenter(new THREE.Vector3());
    model.position.sub(c);
    const holder = new THREE.Group();
    holder.add(model);
    const pre = new THREE.Group();
    pre.add(holder);
    if (opts.pre) holder.rotation.set(...opts.pre);
    // width across the finger (X) after the pre-rotation, used to scale to the finger
    const size = new THREE.Box3().setFromObject(pre).getSize(new THREE.Vector3());
    const unit = 1 / Math.max(size.x, 1e-6);
    holder.scale.setScalar(unit);
    const ringObj = new THREE.Group();
    ringObj.add(pre);
    ringObj.renderOrder = 1;
    scene.add(ringObj);

    // invisible finger: writes depth only, so the back of the band disappears behind it
    const finger = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.5, 1, 32),
      new THREE.MeshBasicMaterial({ colorWrite: false })
    );
    finger.renderOrder = 0;
    scene.add(finger);

    const m = new THREE.Matrix4();
    const vd = new THREE.Vector3();
    const vu = new THREE.Vector3();
    const vs = new THREE.Vector3();
    return {
      resize(w, h) {
        renderer.setSize(w, h, false);
        camera.left = 0;
        camera.right = w;
        camera.top = 0;
        camera.bottom = -h;
        camera.updateProjectionMatrix();
      },
      // pose: centre (x, y, z in canvas px, y down), axis d and face direction u (unit vectors), width w in px
      draw(pose) {
        if (!pose) {
          ringObj.visible = finger.visible = false;
          renderer.render(scene, camera);
          return;
        }
        ringObj.visible = finger.visible = true;
        vd.set(pose.d.x, -pose.d.y, pose.d.z).normalize();
        vu.set(pose.u.x, -pose.u.y, pose.u.z);
        vu.addScaledVector(vd, -vu.dot(vd)).normalize();
        vs.crossVectors(vd, vu).normalize();
        m.makeBasis(vs, vd, vu);
        ringObj.quaternion.setFromRotationMatrix(m);
        finger.quaternion.copy(ringObj.quaternion);
        ringObj.position.set(pose.x, -pose.y, pose.z);
        finger.position.copy(ringObj.position);
        ringObj.scale.setScalar(pose.w * (opts.scale || 0.95));
        finger.scale.set(pose.w * 0.92, pose.w * 4, pose.w * 0.92);
        renderer.render(scene, camera);
      },
    };
  };

  window.__iceTryon3D = make3D; // exposed for testing on a still photo

  const open = async (btn) => {
    const t = JSON.parse(btn.dataset.labels);
    const kind = KIND_TASK[btn.dataset.kind] ? btn.dataset.kind : 'ring';
    const piece = new Image();
    piece.src = btn.dataset.ring;
    const scale = parseFloat(btn.dataset.scale || '1');

    const ui = document.createElement('div');
    ui.className = 'ice-tryon';
    ui.innerHTML = `
      <div class="ice-tryon__stage">
        <video playsinline muted autoplay></video>
        <canvas></canvas>
        <canvas class="ice-tryon__gl"></canvas>
        <span class="ice-tryon__badge" hidden></span>
        <p class="ice-tryon__status">${t.loading}</p>
      </div>
      <div class="ice-tryon__bar">
        ${kind === 'ring' ? `<div class="ice-tryon__fingers" role="group" aria-label="${t.finger}">
          <button type="button" data-f="index">${t.index}</button>
          <button type="button" data-f="middle">${t.middle}</button>
          <button type="button" data-f="ring" class="is-active">${t.ring}</button>
          <button type="button" data-f="pinky">${t.pinky}</button>
        </div>` : '<div></div>'}
        <div class="ice-tryon__actions">
          <button type="button" class="ice-tryon__icon" data-flip aria-label="${t.flip}">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h11a5 5 0 0 1 0 10H9"/><path d="M7 4 4 7l3 3"/></svg>
          </button>
          <button type="button" class="ice-tryon__icon" data-close aria-label="${t.close}">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>
          </button>
        </div>
        <p class="ice-tryon__hint">${t.hint}</p>
      </div>`;
    document.body.appendChild(ui);
    document.documentElement.classList.add('ice-tryon-open');

    const video = ui.querySelector('video');
    const canvas = ui.querySelector('canvas');
    const ctx = canvas.getContext('2d');
    const gl = ui.querySelector('.ice-tryon__gl');
    const modelUrl = kind === 'ring' ? btn.dataset.model : null;
    let three = null;
    let tracker = null;
    const badge = ui.querySelector('.ice-tryon__badge');
    const status = ui.querySelector('.ice-tryon__status');
    let finger = 'ring';
    // hands are easier to film with the back camera; neck and ears are a selfie
    const mobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    let facing = mobile && (kind === 'ring' || kind === 'wrist') ? 'environment' : 'user';
    let stream = null;
    let raf = 0;
    let alive = true;
    let smooth = {};
    let lastSeen = 0;

    const stop = () => {
      if (stream) stream.getTracks().forEach((tr) => tr.stop());
      stream = null;
    };
    const close = () => {
      alive = false;
      cancelAnimationFrame(raf);
      stop();
      ui.remove();
      document.documentElement.classList.remove('ice-tryon-open');
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    ui.querySelector('[data-close]').addEventListener('click', close);

    ui.querySelectorAll('[data-f]').forEach((b) =>
      b.addEventListener('click', () => {
        finger = b.dataset.f;
        smooth = {};
        ui.querySelectorAll('[data-f]').forEach((x) => x.classList.toggle('is-active', x === b));
      })
    );

    const start = async () => {
      stop();
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (e) {
        status.textContent = t.denied;
        return false;
      }
      video.srcObject = stream;
      await video.play().catch(() => {});
      ui.classList.toggle('is-mirrored', facing === 'user');
      return true;
    };
    ui.querySelector('[data-flip]').addEventListener('click', async () => {
      facing = facing === 'user' ? 'environment' : 'user';
      smooth = {};
      await start();
    });

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      status.textContent = t.unsupported;
      return;
    }
    if (!(await start())) return;
    status.textContent = t.preparing;

    // follow the target smoothly; each placement keeps its own history (left / right ear)
    const follow = (key, target) => {
      const s = smooth[key];
      if (!s) return (smooth[key] = { ...target });
      const k = 0.45;
      s.x = lerp(s.x, target.x, k);
      s.y = lerp(s.y, target.y, k);
      let d = target.ang - s.ang;
      d = Math.atan2(Math.sin(d), Math.cos(d));
      s.ang += d * k;
      s.w = lerp(s.w, target.w, k);
      s.top = target.top;
      s.flip = target.flip;
      return s;
    };
    const drawPiece = (p) => {
      const w = p.w * scale;
      const h = (w * piece.naturalHeight) / piece.naturalWidth;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.ang);
      if (p.flip) ctx.scale(-1, 1);
      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = w * 0.06;
      ctx.shadowOffsetY = w * 0.03;
      // hanging pieces start just above their anchor (the hook goes through the lobe)
      ctx.drawImage(piece, -w / 2, p.top ? -h * 0.03 : -h / 2, w, h);
      ctx.restore();
    };

    const loop = () => {
      if (!alive) return;
      raf = requestAnimationFrame(loop);
      if (video.readyState < 2) return;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const cw = (canvas.width = canvas.clientWidth * devicePixelRatio);
      const ch = (canvas.height = canvas.clientHeight * devicePixelRatio);
      // "cover" mapping from video pixels to canvas pixels
      const s = Math.max(cw / vw, ch / vh);
      const ox = (cw - vw * s) / 2;
      const oy = (ch - vh * s) / 2;
      const mirror = facing === 'user';
      ctx.clearRect(0, 0, cw, ch);
      if (!tracker) return;
      let res = null;
      try {
        res = tracker.detectForVideo(video, performance.now());
      } catch (e) {
        return;
      }
      const lm = firstLandmarks(res);
      const P = (i) => {
        let x = lm[i].x * vw * s + ox;
        const y = lm[i].y * vh * s + oy;
        if (mirror) x = cw - x;
        return { x, y };
      };
      const placed = lm ? place(kind, lm, P, finger) : [];
      const shown = [];
      if (placed.length) {
        // ear placements are keyed by side, so one ear can vanish while the other keeps its history
        placed.forEach((p, i) => shown.push(follow(kind === 'ear' ? (p.flip ? 'r' : 'l') : i, p)));
        lastSeen = performance.now();
        status.hidden = true;
      } else if (performance.now() - lastSeen > 600) {
        smooth = {};
        status.hidden = false;
        status.textContent = t.show;
      }
      if (three) {
        if (gl.width !== cw || gl.height !== ch) three.resize(cw, ch);
        let pose = null;
        if (shown[0] && lm) {
          const sm = shown[0];
          const [a, b] = FINGERS[finger];
          const Z = (i) => -lm[i].z * vw * s;
          const P3 = (i) => ({ ...P(i), z: Z(i) });
          const A = P3(a), B = P3(b), W = P3(0), I = P3(5), K = P3(17);
          const d = { x: B.x - A.x, y: B.y - A.y, z: B.z - A.z };
          // back-of-hand normal from wrist / index knuckle / little-finger knuckle
          const v1 = { x: I.x - W.x, y: I.y - W.y, z: I.z - W.z };
          const v2 = { x: K.x - W.x, y: K.y - W.y, z: K.z - W.z };
          let u = { x: v1.y * v2.z - v1.z * v2.y, y: v1.z * v2.x - v1.x * v2.z, z: v1.x * v2.y - v1.y * v2.x };
          // keep the signet face turned towards the camera (we look at the back of the hand)
          if (u.z < 0) u = { x: -u.x, y: -u.y, z: -u.z };
          pose = { x: sm.x, y: sm.y, z: lerp(A.z, B.z, 0.42), d, u, w: sm.w };
        }
        three.draw(pose);
      } else if (piece.complete && piece.naturalWidth) {
        shown.forEach(drawPiece);
      }
    };
    loop();
    try {
      tracker = await loadTracker(KIND_TASK[kind]);
    } catch (e) {
      status.hidden = false;
      status.textContent = t.unsupported;
      const why = document.createElement('small');
      why.className = 'ice-tryon__why';
      why.textContent = String((e && e.message) || e).slice(0, 160);
      status.appendChild(why);
      return;
    }
    if (modelUrl) {
      try {
        three = await withTimeout(
          make3D(gl, modelUrl, {
            pre: (btn.dataset.pre || '').split(',').filter(Boolean).map(Number),
            scale: parseFloat(btn.dataset.scale3d || '0.95'),
            color: btn.dataset.color,
          }),
          60000
        );
        badge.textContent = '3D';
      } catch (e) {
        three = null;
        // keep the flat photo version, and say why in small print (helps support)
        badge.textContent = '2D';
        badge.title = String((e && e.message) || e).slice(0, 160);
        const why = document.createElement('small');
        why.className = 'ice-tryon__why';
        why.textContent = '3D : ' + badge.title;
        ui.querySelector('.ice-tryon__hint').appendChild(why);
      }
      badge.hidden = false;
    }
    if (alive) status.textContent = t.show;
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ice-tryon]');
    if (!btn) return;
    e.preventDefault();
    open(btn);
  });
})();
